import { useCreateTeamRequestMutation } from 'features/TeamChat/api/teamChat.api'
import { teamErrorText } from 'features/TeamChat/api/teamChat.transform'
import type { TeamMember } from 'features/TeamChat/api/teamChat.types'
import { REQUEST_MAX_LENGTH, TEAM_COPY } from 'features/TeamChat/teamChat.constants'
import styles from 'features/TeamChat/teamChat.module.scss'

import { helpPlan } from '../chat/mentionChips'
import { TeamComposer } from '../TeamComposer'
import { useTeamDrafts } from '../TeamDrafts'
import type { RequestDraft } from '../TeamDrafts'

type Props = { members: TeamMember[]; onClose: () => void }

// The chat's composer, sending a request instead of a message; the X leaves it.
export const RequestComposer = ({ members, onClose }: Props) => {
  const { request, setRequest, requestSending } = useTeamDrafts()
  const { title, error, isSending } = request
  const [createRequest] = useCreateTeamRequestMutation()
  const update = (patch: Partial<RequestDraft>) => setRequest((s) => ({ ...s, ...patch }))

  // One request at a time; on success the composer closes, on failure the text stays.
  const send = async (text: string, closeOnSuccess: boolean) => {
    if (requestSending.current) return
    requestSending.current = true
    update({ isSending: true, error: '' })
    try {
      await createRequest({ title: text }).unwrap()
      if (closeOnSuccess) onClose()
    } catch (e) {
      update({ error: teamErrorText(e, TEAM_COPY.SEND_FAILED) })
    } finally {
      requestSending.current = false
      update({ isSending: false })
    }
  }

  const submit = () => {
    const trimmed = title.trim()
    if (!trimmed) return update({ error: TEAM_COPY.REQUEST_REQUIRED })
    void send(trimmed, true)
  }

  const askForHelp = () => {
    const plan = helpPlan(title)
    void send(plan.title, plan.clearDraft)
  }

  return (
    <div className={styles.slideUp}>
      <TeamComposer
        id='team-request-title'
        value={title}
        members={members}
        placeholder={TEAM_COPY.REQUEST_PLACEHOLDER}
        ariaLabel={TEAM_COPY.WHAT_DO_YOU_NEED}
        sendLabel={TEAM_COPY.SEND_TO_TEAM}
        maxLength={REQUEST_MAX_LENGTH}
        error={error}
        busy={isSending}
        helpBusy={isSending}
        focusOnOpen
        onChange={(v) => update({ title: v, error: '' })}
        onSubmit={submit}
        onHelp={askForHelp}
        onCancel={onClose}
      />
    </div>
  )
}
